import { LogOut, Menu } from 'lucide-react'
import { useNavigate } from 'react-router'

import { ROLE_LABELS, useAuth } from '@/features/auth'
import { ThemeToggle } from '@/shared/components/common/ThemeToggle'
import { Button } from '@/shared/components/ui/button'
import { APP_VERSION } from '@/shared/config/appVersion'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

import { ROUTES } from '../router/routes'

interface HeaderProps {
  onOpenMenu: () => void
}

/** Barra superior: menú móvil y datos del usuario. */
export function Header({ onOpenMenu }: HeaderProps) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const displayIdentity = user?.fullName?.trim() || user?.email.trim() || 'Usuario'
  const avatarInitial = displayIdentity.charAt(0).toUpperCase()

  const handleSignOut = () => {
    signOut()
    void navigate(ROUTES.LOGIN, { replace: true })
  }

  return (
    <header className="bg-background/80 flex h-16 shrink-0 items-center justify-between border-b px-4 backdrop-blur-sm sm:px-6">
      {/* El botón de menú solo existe en móvil: en escritorio la barra lateral
          está siempre visible. `lg:hidden` lo oculta a partir de esa medida. */}
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenMenu}>
        <span className="sr-only">Abrir menú de navegación</span>
        <Menu className="size-5" aria-hidden="true" />
      </Button>

      {/* Empuja los controles de la derecha cuando el botón de menú está oculto. */}
      <div className="flex-1" />

      <div className="flex items-center gap-1">
        <ThemeToggle />

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2.5 pr-2 pl-2">
                <span className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-full text-xs font-semibold">
                  {avatarInitial}
                </span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm leading-tight font-medium">{displayIdentity}</span>
                  <span className="text-muted-foreground block text-xs leading-tight">
                    {ROLE_LABELS[user.role]}
                  </span>
                </span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium">{displayIdentity}</p>
                <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                <p className="text-muted-foreground mt-1 text-xs">{ROLE_LABELS[user.role]}</p>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 size-4" aria-hidden="true" />
                Cerrar sesión
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuLabel className="px-2 py-1.5 text-xs font-normal">
                <span className="text-muted-foreground block">Alto Palermo</span>
                <span className="text-muted-foreground block">v{APP_VERSION}</span>
              </DropdownMenuLabel>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
